/**
 * Label Filter Styled Components
 *
 * Shared styled components for the LabelFilterUI component.
 * Supports three variants: inline, popover, and card.
 */

import styled from 'styled-components'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

/**
 * Container for popover variant
 */
export const PopoverContainer = styled.div`
  width: 280px;
  padding: 12px;
  background: var(--color-bg-elevated);
`

/**
 * Header for popover variant
 */
export const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

/**
 * Title with icon for popover variant
 */
export const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

/**
 * Container for the chips and add button
 */
export const ChipsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-height: 32px;
  flex: 1;
  min-width: 0;
`

/**
 * Add label button - primary style (for inline variant)
 */
export const AddFilterButtonPrimary = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: var(--color-primary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: white;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`

/**
 * Add label button - subtle style (for popover variant)
 */
export const AddFilterButtonSubtle = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  min-width: 20px;
  min-height: 20px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  color: var(--color-text-primary);

  &:hover:not(:disabled) {
    background: var(--color-bg-hover);
    transform: scale(1.05);
  }
`

/**
 * Clear filters button
 */
export const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-secondary);
  }
`

/**
 * Empty state message
 */
export const EmptyMessage = styled.div`
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  padding: 4px 0;
`

/**
 * No filter text for inline variant
 */
export const NoFilterText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`

/**
 * Dropdown label item container
 */
export const DropdownLabelItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

/**
 * Color swatch for dropdown
 */
export const ColorSwatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(props) => props.$color};
  border: 1px solid rgba(0, 0, 0, 0.1);
`

/**
 * Container for inline variant (horizontal layout)
 */
export const InlineContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

/**
 * Section wrapper for inline variant
 */
export const InlineSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

/**
 * Label text for inline variant
 */
export const InlineLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  white-space: nowrap;
`

/**
 * Card variant label text
 */
export const CardLabelText = styled.div`
  font-weight: ${FONT_WEIGHT.semibold};
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-primary);
`
